/**
 * subscription-collection.js
 * Handles add-to-cart with selling plan (subscription) for the
 * Subscription Collection section.
 *
 * Upload this file to: Assets/subscription-collection.js
 */

(function () {
  'use strict';

  // ─── Read section config ──────────────────────────────────────────────────
  function getSectionConfig() {
    const dataEl = document.querySelector('[data-subscription-section-data]');
    if (!dataEl) return {};
    try {
      return JSON.parse(dataEl.textContent);
    } catch (e) {
      return {};
    }
  }

  const config = getSectionConfig();

  // ─── Cart AJAX helper ─────────────────────────────────────────────────────
  async function addToCart(variantId, sellingPlanId) {
    const body = {
      id: parseInt(variantId, 10),
      quantity: 1,
    };

    if (sellingPlanId) {
      body.selling_plan = parseInt(sellingPlanId, 10);
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.description || 'Add to cart failed');
    }

    return response.json();
  }

  // ─── After-add action ─────────────────────────────────────────────────────
  function handlePostAdd() {
    const action = config.cart_action || 'drawer';

    if (action === 'redirect') {
      window.location.href = '/cart';
      return;
    }

    if (action === 'drawer') {
      // ── Flex theme: primary method ──
      // Flex exposes jsAjaxCart.updateView() on the global Shopify.theme object.
      // This refreshes the cart drawer contents and opens it.
      if (
        window.Shopify &&
        window.Shopify.theme &&
        window.Shopify.theme.jsAjaxCart &&
        typeof window.Shopify.theme.jsAjaxCart.updateView === 'function'
      ) {
        window.Shopify.theme.jsAjaxCart.updateView();
        return;
      }

      // ── Fallback 1: click the cart icon ──
      // Works when Flex's JS is listening for clicks on the cart trigger
      const cartToggle =
        document.querySelector('[data-cart-toggle]') ||
        document.querySelector('.jsCartCount') ||
        document.querySelector('[data-header-cart]') ||
        document.querySelector('a[href="/cart"].cart-toggle');

      if (cartToggle) {
        cartToggle.click();
        return;
      }

      // ── Fallback 2: redirect to cart ──
      window.location.href = '/cart';
    }

    // action === 'none': feedback message on card is enough
  }

  // ─── Show feedback on card ────────────────────────────────────────────────
  function showFeedback(feedbackEl, message, isError) {
    if (!feedbackEl) return;
    feedbackEl.textContent = message;
    feedbackEl.classList.toggle('is-error', !!isError);

    // Clear after 3 seconds
    setTimeout(() => {
      feedbackEl.textContent = '';
      feedbackEl.classList.remove('is-error');
    }, 3000);
  }

  // ─── Get active selling plan for a card ──────────────────────────────────
  function getActiveSellingPlanId(card, defaultSellingPlanId) {
    const select = card.querySelector('[data-selling-plan-select]');
    if (select) {
      return select.value;
    }
    return defaultSellingPlanId;
  }

  // ─── Handle subscription ATC click ───────────────────────────────────────
  async function handleSubscriptionClick(btn, card) {
    const variantId = btn.dataset.variantId;
    const defaultPlanId = btn.dataset.sellingPlanId;
    const sellingPlanId = getActiveSellingPlanId(card, defaultPlanId);
    const feedbackEl = card.querySelector('[data-atc-feedback]');

    if (!variantId) return;

    // Loading state
    btn.disabled = true;
    btn.classList.add('is-loading');
    const originalText = btn.textContent;
    btn.textContent = 'Adding…';

    try {
      await addToCart(variantId, sellingPlanId);

      // Success
      showFeedback(feedbackEl, config.added_message || 'Added to cart!', false);
      handlePostAdd();
    } catch (err) {
      console.error('[subscription-collection]', err);
      showFeedback(feedbackEl, config.error_message || 'Something went wrong. Please try again.', true);
    } finally {
      btn.disabled = false;
      btn.classList.remove('is-loading');
      btn.textContent = originalText;
    }
  }

  // ─── Handle one-time ATC click ────────────────────────────────────────────
  async function handleOnetimeClick(btn, card) {
    const variantId = btn.dataset.variantId;
    const feedbackEl = card.querySelector('[data-atc-feedback]');

    if (!variantId) return;

    btn.disabled = true;
    const originalText = btn.textContent;
    btn.textContent = 'Adding…';

    try {
      await addToCart(variantId, null); // no selling plan = one-time purchase
      showFeedback(feedbackEl, config.added_message || 'Added to cart!', false);
      handlePostAdd();
    } catch (err) {
      console.error('[subscription-collection]', err);
      showFeedback(feedbackEl, config.error_message || 'Something went wrong. Please try again.', true);
    } finally {
      btn.disabled = false;
      btn.textContent = originalText;
    }
  }

  // ─── Bind events ──────────────────────────────────────────────────────────
  function init() {
    const grid = document.querySelector('[data-subscription-grid]');
    if (!grid) return;

    // Use event delegation on the grid for performance
    grid.addEventListener('click', function (e) {
      // Subscription ATC button
      const subBtn = e.target.closest('[data-subscription-atc]');
      if (subBtn) {
        const card = subBtn.closest('.subscription-card');
        handleSubscriptionClick(subBtn, card);
        return;
      }

      // One-time ATC button
      const onetimeBtn = e.target.closest('[data-onetime-atc]');
      if (onetimeBtn) {
        const card = onetimeBtn.closest('.subscription-card');
        handleOnetimeClick(onetimeBtn, card);
        return;
      }
    });
  }

  // ─── Boot ─────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
